import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { APIProvider, Map as GoogleMap, AdvancedMarker, Pin } from '@vis.gl/react-google-maps';
import { AnimatePresence, motion } from 'framer-motion';
import { Camera, FileDown, Video, MapPin, Home, X, Lock, ExternalLink, Download, Play, LayoutList } from 'lucide-react';
import type { PlanosProject, PlanosFloor, Hotspot, HotspotIcon, ViewerSettings } from '../types';
import { rotateImageToDataUrl, useGroundOverlay } from '../utils/floorPlanOverlay';
import { getProjectPublic } from '../services/supabase';
import { trackEvent } from '../services/analytics';
import ZoomableFloorPlan from './ZoomableFloorPlan';
import Viewer360 from './Viewer360';
import { isEquirectangular } from '../utils/isEquirectangular';
import { getVideoEmbedUrl } from '../utils/video';

const LazyMini360Preview = lazy(() => import('./Mini360Preview'));

/** Video iframe with black loading overlay until embed loads */
function VideoEmbedWithLoader({ embedUrl }: { embedUrl: string }) {
  const [loading, setLoading] = useState(true);
  useEffect(() => setLoading(true), [embedUrl]);
  return (
    <div className="w-full rounded-xl overflow-hidden bg-black border border-slate-600 aspect-video relative">
      <iframe
        src={embedUrl}
        title="Video"
        className="w-full h-full absolute inset-0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        onLoad={() => setLoading(false)}
      />
      {loading && (
        <div className="absolute inset-0 bg-black flex flex-col items-center justify-center gap-3 z-10">
          <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" aria-hidden />
          <span className="text-sm text-white/70">Loading video...</span>
        </div>
      )}
    </div>
  );
}

function Icon360Viewer({ size = 14, className, style }: { size?: number; className?: string; style?: React.CSSProperties }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} style={style}>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.8" />
      <ellipse cx="12" cy="12" rx="4.5" ry="10" stroke="currentColor" strokeWidth="1.2" />
      <path d="M2.5 12h19" stroke="currentColor" strokeWidth="1.2" />
      <path d="M4 7h16M4 17h16" stroke="currentColor" strokeWidth="0.8" opacity="0.5" />
    </svg>
  );
}

function HotspotIconInList({ type, color, iconUrl }: { type?: HotspotIcon; color?: string; iconUrl?: string | null }) {
  const t = type ?? 'camera';
  const props = { size: 14, className: 'opacity-70', style: { color: color ?? '#ffffff' } as React.CSSProperties };
  if (t === 'custom' && iconUrl) {
    return <img src={iconUrl} alt="" className="w-3.5 h-3.5 object-contain opacity-70 rounded-sm" style={{ maxWidth: 14, maxHeight: 14 }} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />;
  }
  switch (t) {
    case 'pano360': return <Icon360Viewer {...props} />;
    case 'file': return <FileDown {...props} />;
    case 'video': return <Video {...props} />;
    case 'map-pin': return <MapPin {...props} />;
    case 'home': return <Home {...props} />;
    default: return <Camera {...props} />;
  }
}

/** Loads image, detects 2:1 equirectangular, calls onIs360(true) if so; otherwise renders flat img */
function HotspotImage({ src, alt, onIs360 }: { src: string; alt: string; onIs360: (v: boolean) => void }) {
  const [loaded, setLoaded] = useState(false);
  const [is360, setIs360] = useState<boolean | null>(null);

  useEffect(() => {
    setLoaded(false);
    setIs360(null);
    const img = new Image();
    img.onload = () => {
      setLoaded(true);
      const detected = isEquirectangular(img.naturalWidth, img.naturalHeight);
      setIs360(detected);
      if (detected) onIs360(true);
    };
    img.src = src;
  }, [src, onIs360]);

  if (!loaded || is360) {
    return (
      <div className="w-full h-64 sm:h-80 rounded-xl border border-slate-600 flex items-center justify-center text-slate-400 text-sm">
        {!loaded ? 'Loading...' : null}
      </div>
    );
  }
  return (
    <motion.img
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      src={src}
      alt={alt}
      className="w-full max-h-[80vh] object-contain rounded-xl"
    />
  );
}

const MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';
const MAP_ID = import.meta.env.VITE_GOOGLE_MAP_ID || '';

function getViewerFloors(project: PlanosProject): PlanosFloor[] {
  if (project.floors && project.floors.length > 0) return project.floors;
  if (project.floorplan_image_url)
    return [{ id: 'legacy', label: 'Ground', image_url: project.floorplan_image_url, overlay_bounds: project.overlay_bounds ?? null }];
  return [{ id: 'single', label: 'Ground', image_url: null, overlay_bounds: null }];
}

interface PlanosViewerProps {
  slugOrId: string;
  isEmbed?: boolean;
}

const PlanosViewer: React.FC<PlanosViewerProps> = ({ slugOrId, isEmbed }) => {
  const [project, setProject] = useState<PlanosProject | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Hotspot | null>(null);
  const [is360Image, setIs360Image] = useState(false);
  const [mapRef, setMapRef] = useState<google.maps.Map | null>(null);
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [hoveredHotspotId, setHoveredHotspotId] = useState<string | null>(null);
  const [show360Fullscreen, setShow360Fullscreen] = useState(false);
  const [embedStarted, setEmbedStarted] = useState(false);
  const [embedRoomsOpen, setEmbedRoomsOpen] = useState(false);
  const [floorplanImageLoadFailed, setFloorplanImageLoadFailed] = useState(false);
  const [hoverPreviewVisible, setHoverPreviewVisible] = useState(false);
  const [hoverPreviewAnchor, setHoverPreviewAnchor] = useState<{ x: number; y: number } | null>(null);
  const is360CacheRef = useRef<Map<string, boolean>>(new Map());
  const [is360CacheVer, setIs360CacheVer] = useState(0);
  const viewerFloors = project ? getViewerFloors(project) : [];
  const [selectedFloorId, setSelectedFloorId] = useState<string>('');
  const [viewerOverlayImageUrl, setViewerOverlayImageUrl] = useState<string | null>(null);

  // Pre-detect which hotspot photos are equirectangular so hover preview can show mini 360
  useEffect(() => {
    if (!project) return;
    const hs = project.hotspots ?? [];
    for (const h of hs) {
      if (!h.photo_url || is360CacheRef.current.has(h.photo_url)) continue;
      const img = new Image();
      const url = h.photo_url;
      img.onload = () => {
        is360CacheRef.current.set(url, isEquirectangular(img.naturalWidth, img.naturalHeight));
        setIs360CacheVer((v) => v + 1);
      };
      img.src = url;
    }
  }, [project?.id, project?.hotspots]);

  // Show small hover preview after a short delay so it doesn’t flash on quick mouse moves
  useEffect(() => {
    if (!hoveredHotspotId) {
      setHoverPreviewVisible(false);
      setHoverPreviewAnchor(null);
      return;
    }
    const t = setTimeout(() => setHoverPreviewVisible(true), 300);
    return () => clearTimeout(t);
  }, [hoveredHotspotId]);

  useEffect(() => {
    if (project && viewerFloors.length > 0) setSelectedFloorId((prev) => (viewerFloors.some((f) => f.id === prev) ? prev : viewerFloors[0].id));
  }, [project?.id, project?.floorplan_image_url ?? '', project?.floors?.length ?? 0]);

  const floorplanImageUrl =
    (viewerFloors.find((f) => f.image_url)?.image_url) ?? (project?.floorplan_image_url ?? null);
  useEffect(() => {
    setFloorplanImageLoadFailed(false);
  }, [floorplanImageUrl]);

  const isFloorplanMode = (project?.view_mode ?? 'map') === 'floorplan';
  const currentViewerFloor = viewerFloors.find((f) => f.id === selectedFloorId);
  const viewerFloorplanUrl = project ? (currentViewerFloor?.image_url ?? project.floorplan_image_url ?? '') : '';
  const viewerOverlayBounds = project ? (currentViewerFloor?.overlay_bounds ?? project.overlay_bounds ?? null) : null;
  const viewerMapHeading = project?.map_heading ?? 0;
  useEffect(() => {
    if (!viewerFloorplanUrl) {
      setViewerOverlayImageUrl(null);
      return;
    }
    let cancelled = false;
    rotateImageToDataUrl(viewerFloorplanUrl, -viewerMapHeading).then((url) => {
      if (!cancelled) setViewerOverlayImageUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [viewerFloorplanUrl, viewerMapHeading]);
  const effectiveViewerOverlayUrl =
    viewerOverlayImageUrl ?? (viewerMapHeading === 0 ? (viewerFloorplanUrl || null) : null);
  useGroundOverlay(!project || isFloorplanMode ? null : mapRef, effectiveViewerOverlayUrl, viewerOverlayBounds);

  useEffect(() => { setIs360Image(false); }, [selected?.id]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showMapModal) setShowMapModal(false);
        else if (selected) { setSelected(null); setIs360Image(false); setShow360Fullscreen(false); }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showMapModal, selected]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await getProjectPublic(slugOrId);
        if (cancelled) return;
        if (data?.password) {
          const stored = sessionStorage.getItem(`planos_pass_${data.id}`);
          if (stored === data.password) {
            setProject(data);
          } else {
            setProject(data);
            setPasswordRequired(true);
          }
        } else {
          setProject(data);
        }
      } catch (_) {
        if (!cancelled) setProject(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [slugOrId]);

  useEffect(() => {
    if (project && !passwordRequired) trackEvent(project.id, 'view');
  }, [project?.id, passwordRequired]);

  // In embed mode, make html/body/root fill the iframe so the layout doesn’t leave a large empty gap below
  useEffect(() => {
    if (!isEmbed) return;
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    const prev = { html: html.style.height, body: body.style.height, root: root?.style.height ?? '' };
    html.style.height = '100%';
    body.style.height = '100%';
    if (root) root.style.height = '100%';
    return () => {
      html.style.height = prev.html;
      body.style.height = prev.body;
      if (root) root.style.height = prev.root;
    };
  }, [isEmbed]);

  // Mobile embeds should be interactive immediately; many hosts/phone browsers make the
  // "Click to explore" gate feel unresponsive or block first taps.
  useEffect(() => {
    if (!isEmbed) {
      setEmbedStarted(false);
      return;
    }
    const coarsePointer =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(pointer: coarse)').matches;
    if (coarsePointer) {
      setEmbedStarted(true);
    }
  }, [isEmbed]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: '#1a1a1a' }}>
        <div className="flex items-center gap-3 text-slate-400">
          <MapPin className="animate-pulse" style={{ color: '#FF3B30' }} />
          <span>Loading spatial map...</span>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: '#1a1a1a' }}>
        <p className="text-slate-400">This plan is not published or does not exist.</p>
      </div>
    );
  }

  if (passwordRequired && project) {
    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (passwordInput === project.password) {
        sessionStorage.setItem(`planos_pass_${project.id}`, passwordInput);
        setPasswordRequired(false);
        setPasswordError(false);
      } else {
        setPasswordError(true);
      }
    };
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#1a1a1a' }}>
        <div className="w-full max-w-sm p-6 rounded-2xl border" style={{ backgroundColor: '#2F2F2F', borderColor: '#3d3d3d' }}>
          <div className="flex items-center gap-2 mb-4">
            <Lock size={24} className="text-[#FF3B30]" />
            <h2 className="text-lg font-semibold text-white">Password required</h2>
          </div>
          <p className="text-slate-400 mb-6 text-sm">This plan is password protected.</p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="password"
              value={passwordInput}
              onChange={(e) => { setPasswordInput(e.target.value); setPasswordError(false); }}
              placeholder="Enter password"
              className={`w-full px-4 py-3 rounded-xl bg-black border text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[#FF3B30]/50 ${passwordError ? 'border-red-500' : 'border-slate-700'}`}
              autoFocus
            />
            {passwordError && <p className="text-xs text-red-400">Incorrect password</p>}
            <button type="submit" className="w-full py-3 rounded-xl text-white font-semibold" style={{ backgroundColor: '#FF3B30' }}>
              Continue
            </button>
          </form>
        </div>
      </div>
    );
  }

  const hotspots = project.hotspots ?? [];
  const floorplanHotspots = isFloorplanMode ? hotspots.filter((h) => h.x_pct != null && h.y_pct != null) : hotspots;
  const settings: ViewerSettings = project.viewer_settings ?? {};
  const primary = settings.primary_color ?? '#FF3B30';
  const bg = settings.background_color ?? '#1a1a1a';
  const sidebarBg = settings.accent_color ?? '#2F2F2F';
  const textColor = settings.text_color ?? '#ffffff';
  const logoSize = settings.logo_size ?? 'small';
  const showPoweredBy = settings.show_powered_by !== false;

  const logoClass = logoSize === 'large' ? 'h-12 max-w-[220px]' : logoSize === 'medium' ? 'h-9 max-w-[180px]' : 'h-7 max-w-[140px]';
  const showRoomsList = settings.show_rooms_list !== false;
  const roomsListPosition = settings.rooms_list_position ?? 'left';

  const handleHotspotClick = (h: Hotspot) => {
    setSelected(h);
    trackEvent(project.id, 'hotspot_click', { hotspotId: h.id });
  };

  return (
    <div className={`min-h-screen flex flex-col ${isEmbed ? 'min-h-0 h-full' : ''}`} style={{ backgroundColor: bg }}>
      {/* Header — hide in embed for cleaner look */}
      {!isEmbed && (
      <>
      <header className="border-b px-4 py-3 flex items-center justify-between shrink-0 relative" style={{ backgroundColor: sidebarBg, borderColor: '#3d3d3d' }}>
        <div className="flex items-center gap-3 min-w-0 relative">
          {settings.logo_watermark_url && (
            <img
              src={settings.logo_watermark_url}
              alt=""
              aria-hidden
              className="absolute left-0 top-1/2 -translate-y-1/2 h-16 w-auto max-w-[200px] object-contain object-left pointer-events-none select-none"
              style={{ opacity: Math.min(1, Math.max(0, settings.logo_watermark_opacity ?? 0.2)) }}
            />
          )}
          {settings.logo_url ? (
            <img src={settings.logo_url} alt="Logo" className={`${logoClass} object-contain object-left shrink-0 relative z-10`} />
          ) : (
            <img src="/Planos Logo Red.png" alt="Planos.studio" className={`${logoSize === 'large' ? 'h-12' : logoSize === 'medium' ? 'h-9' : 'h-7'} w-auto object-contain shrink-0 relative z-10`} />
          )}
          <span className="text-sm font-semibold truncate relative z-10" style={{ color: textColor }}>{project.title}</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {project.address && <span className="text-xs hidden sm:block truncate max-w-[200px] opacity-80" style={{ color: textColor }}>{project.address}</span>}
          <button
            onClick={() => setShowMapModal(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors hover:opacity-90"
            style={{ backgroundColor: primary, color: 'white' }}
            title={project.google_coords ? 'View on Google Maps' : 'Location'}
          >
            <MapPin size={14} />
            <span className="hidden sm:inline">Map</span>
          </button>
        </div>
      </header>

      {/* Google Maps / Location modal — always show when Map clicked, even if no coords */}
      <AnimatePresence>
        {showMapModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}
            onClick={() => setShowMapModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              className="w-full max-w-2xl rounded-2xl border overflow-hidden relative shadow-2xl"
              style={{ backgroundColor: sidebarBg, borderColor: '#3d3d3d' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: '#3d3d3d' }}>
                <div>
                  <h3 className="text-sm font-semibold" style={{ color: textColor }}>Location</h3>
                  {project.address && <p className="text-xs truncate max-w-[280px] opacity-80" style={{ color: textColor }}>{project.address}</p>}
                </div>
                <div className="flex items-center gap-2">
                  {project.google_coords ? (
                    <a
                      href={`https://www.google.com/maps?q=${project.google_coords.lat},${project.google_coords.lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors hover:opacity-90"
                      style={{ backgroundColor: primary, color: 'white' }}
                    >
                      <ExternalLink size={14} />
                      Open in Google Maps
                    </a>
                  ) : null}
                      <button onClick={() => setShowMapModal(false)} className="p-2 rounded-lg opacity-80 hover:opacity-100 hover:bg-black/30 transition-colors shrink-0" style={{ color: textColor }} aria-label="Close">
                    <X size={20} />
                  </button>
                </div>
              </div>
              <div className="h-80 sm:h-96 relative bg-black">
                {project.google_coords ? (
                  MAPS_KEY ? (
                    <iframe
                      title="Google Maps"
                      src={`https://www.google.com/maps/embed/v1/place?key=${MAPS_KEY}&q=${project.google_coords.lat},${project.google_coords.lng}&zoom=18`}
                      className="absolute inset-0 w-full h-full border-0"
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  ) : (
                    <a
                      href={`https://www.google.com/maps?q=${project.google_coords.lat},${project.google_coords.lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="absolute inset-0 flex items-center justify-center gap-2 text-slate-400 hover:text-white transition-colors"
                      style={{ color: primary }}
                    >
                      <MapPin size={32} />
                      <span>Open in Google Maps</span>
                    </a>
                  )
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 opacity-80" style={{ color: textColor }}>
                    <MapPin size={40} className="opacity-50" style={{ color: primary }} />
                    <p className="text-sm">No location has been set for this plan.</p>
                    <p className="text-xs opacity-80">The editor can add a map location from the project settings.</p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </>
      )}

      <div className={`flex-1 flex min-h-0 ${roomsListPosition === 'bottom' && showRoomsList && !isEmbed ? 'flex-col' : 'flex-col lg:flex-row'} ${roomsListPosition === 'right' && showRoomsList && !isEmbed ? 'lg:flex-row-reverse' : ''}`}>
        {/* Sidebar room list (left) — hidden when show_rooms_list is false or position is not left */}
        {!isEmbed && showRoomsList && roomsListPosition === 'left' && (
        <aside className="w-full lg:w-64 shrink-0 border-r p-3 overflow-y-auto flex flex-col gap-3" style={{ backgroundColor: sidebarBg, borderColor: '#3d3d3d' }}>
          <p className="text-xs font-semibold mb-2 uppercase tracking-wider opacity-80 shrink-0" style={{ color: textColor }}>Rooms</p>
          <div className="space-y-1">
            {floorplanHotspots.map((h) => (
              <button
                key={h.id}
                onClick={() => { if (!isFloorplanMode) { mapRef?.panTo({ lat: h.lat, lng: h.lng }); mapRef?.setZoom(20); } handleHotspotClick(h); }}
                onMouseEnter={() => setHoveredHotspotId(h.id)}
                onMouseLeave={() => setHoveredHotspotId(null)}
                className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm hover:bg-white/10 transition-colors"
                style={{ color: textColor }}
              >
                <span>{h.room_label}</span>
                <HotspotIconInList type={h.icon_type} color={textColor} iconUrl={h.icon_url} />
              </button>
            ))}
          </div>
        </aside>
        )}

        {/* Map (map mode) or Floor plan (floorplan mode) */}
        <div className="flex-1 min-h-[400px] flex flex-col min-h-0 overflow-hidden relative">
          {/* Embed: Rooms button — opens slide-over panel; hidden in minimal layout */}
          {isEmbed && embedStarted && showRoomsList && (
            <>
              <button
                type="button"
                onClick={() => setEmbedRoomsOpen(true)}
                className="absolute top-3 left-3 z-10 flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium shadow-lg border transition-colors"
                style={{ backgroundColor: sidebarBg, color: textColor, borderColor: '#3d3d3d' }}
                aria-label="Show rooms"
              >
                <LayoutList size={18} />
                <span>Rooms</span>
              </button>
              <AnimatePresence>
                {embedRoomsOpen && (
                  <>
                    <motion.div
                      key="embed-rooms-backdrop"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 z-40 bg-black/40"
                      onClick={() => setEmbedRoomsOpen(false)}
                      aria-hidden
                    />
                    <motion.aside
                      key="embed-rooms-panel"
                      initial={{ x: '-100%' }}
                      animate={{ x: 0 }}
                      exit={{ x: '-100%' }}
                      transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                      className="fixed left-0 top-0 bottom-0 w-72 max-w-[85vw] z-50 border-r p-4 overflow-y-auto flex flex-col gap-3 shadow-xl"
                      style={{ backgroundColor: sidebarBg, borderColor: '#3d3d3d' }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-semibold uppercase tracking-wider opacity-80" style={{ color: textColor }}>Rooms</p>
                        <button type="button" onClick={() => setEmbedRoomsOpen(false)} className="p-2 rounded-lg hover:bg-white/10 transition-colors" style={{ color: textColor }} aria-label="Close rooms">
                          <X size={18} />
                        </button>
                      </div>
                      <div className="space-y-1">
                        {floorplanHotspots.map((h) => (
                          <button
                            key={h.id}
                            onClick={() => { if (!isFloorplanMode) { mapRef?.panTo({ lat: h.lat, lng: h.lng }); mapRef?.setZoom(20); } handleHotspotClick(h); setEmbedRoomsOpen(false); }}
                            onMouseEnter={() => setHoveredHotspotId(h.id)}
                            onMouseLeave={() => setHoveredHotspotId(null)}
                            className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm hover:bg-white/10 transition-colors"
                            style={{ color: textColor }}
                          >
                            <span>{h.room_label}</span>
                            <HotspotIconInList type={h.icon_type} color={textColor} iconUrl={h.icon_url} />
                          </button>
                        ))}
                      </div>
                    </motion.aside>
                  </>
                )}
              </AnimatePresence>
            </>
          )}

          {/* Embed minimal layout: logo + map icon bar when rooms list is hidden */}
          {isEmbed && embedStarted && !showRoomsList && (
            <div
              className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-3 py-2 border-b"
              style={{ backgroundColor: sidebarBg, borderColor: '#3d3d3d' }}
            >
              <div className="flex items-center gap-2 min-w-0">
                {settings.logo_url ? (
                  <img src={settings.logo_url} alt="Logo" className={`${logoClass} object-contain shrink-0`} />
                ) : (
                  <img src="/Planos Logo Red.png" alt="Planos.studio" className={`${logoSize === 'large' ? 'h-10' : logoSize === 'medium' ? 'h-8' : 'h-6'} w-auto object-contain shrink-0`} />
                )}
                <span className="text-sm font-semibold truncate" style={{ color: textColor }}>{project.title}</span>
              </div>
              <button
                onClick={() => setShowMapModal(true)}
                className="shrink-0 flex items-center justify-center w-10 h-10 rounded-lg transition-colors hover:opacity-90"
                style={{ backgroundColor: primary, color: 'white' }}
                title={project.google_coords ? 'View on Google Maps' : 'Location'}
                aria-label="View map"
              >
                <MapPin size={18} />
              </button>
            </div>
          )}

          {/* Embed: subtle play overlay — floor plan visible underneath, compact CTA */}
          {isEmbed && !embedStarted && (
            <button
              type="button"
              onClick={() => setEmbedStarted(true)}
              className="absolute inset-0 z-20 flex flex-col items-center justify-end pb-8 focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-inset"
              style={{ color: project ? (project.viewer_settings?.primary_color ?? '#FF3B30') : undefined }}
              aria-label="Start interactive plan"
            >
              <div className="absolute inset-0 bg-black/35 pointer-events-none" aria-hidden />
              <span className="relative flex items-center gap-2 px-5 py-3 rounded-full bg-black/70 text-white/95 text-sm font-medium shadow-lg border border-white/20 hover:bg-black/80 transition-colors">
                <Play size={20} className="ml-0.5" fill="currentColor" />
                Tap to explore
              </span>
            </button>
          )}
          {isFloorplanMode ? (
            <div className="flex-1 min-h-[360px] min-w-0 flex flex-col overflow-hidden bg-black" style={{ minHeight: '50vh' }}>
              {/* Planos: single floor plan only — no Ground/First floor selector */}
              <div className="flex-1 min-h-0 relative p-4">
                {(() => {
                  if (floorplanImageLoadFailed) {
                    return (
                      <p className="text-slate-400 text-sm absolute inset-0 flex items-center justify-center text-center px-4">
                        Floor plan image could not be loaded. If you own this plan, add the storage policy in Supabase so the image is readable (see planos-studio/STORAGE_POLICY.md).
                      </p>
                    );
                  }
                  if (floorplanImageUrl) {
                    return (
                      <div className="absolute inset-4 min-h-[280px]">
                        <ZoomableFloorPlan
                          imageUrl={floorplanImageUrl}
                          hotspots={floorplanHotspots}
                          onHotspotClick={handleHotspotClick}
                          onHotspotHover={(h, anchor) => {
                            setHoveredHotspotId(h?.id ?? null);
                            setHoverPreviewAnchor(anchor ?? null);
                          }}
                          selectedId={selected?.id}
                          hoveredId={hoveredHotspotId}
                          primaryColor={primary}
                          onImageError={() => setFloorplanImageLoadFailed(true)}
                        />
                      </div>
                    );
                  }
                  return (
                    <p className="text-slate-500 text-sm absolute inset-0 flex items-center justify-center text-center px-4">
                      No floor plan. Open this project in the editor to upload one and save.
                    </p>
                  );
                })()}
              </div>
            </div>
          ) : MAPS_KEY ? (
            <APIProvider apiKey={MAPS_KEY}>
              <GoogleMap
                mapId={MAP_ID}
                defaultCenter={project.google_coords}
                defaultZoom={19}
                defaultHeading={project.map_heading ?? 0}
                gestureHandling="greedy"
                disableDefaultUI
                reuseMaps
                onIdle={(e: any) => setMapRef(e.map)}
                style={{ width: '100%', height: '100%' }}
              >
                {hotspots.map((h) => (
                  <AdvancedMarker key={h.id} position={{ lat: h.lat, lng: h.lng }} onClick={() => handleHotspotClick(h)}>
                    <Pin background={h.icon_color ?? '#dc2626'} glyphColor="#fff" borderColor="#7f1d1d" />
                  </AdvancedMarker>
                ))}
              </GoogleMap>
            </APIProvider>
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-zinc-900 text-slate-400 text-sm">Google Maps not configured.</div>
          )}
        </div>

        {/* Sidebar room list (right) */}
        {!isEmbed && showRoomsList && roomsListPosition === 'right' && (
        <aside className="w-full lg:w-64 shrink-0 border-l p-3 overflow-y-auto flex flex-col gap-3" style={{ backgroundColor: sidebarBg, borderColor: '#3d3d3d' }}>
          <p className="text-xs font-semibold mb-2 uppercase tracking-wider opacity-80 shrink-0" style={{ color: textColor }}>Rooms</p>
          <div className="space-y-1">
            {floorplanHotspots.map((h) => (
              <button
                key={h.id}
                onClick={() => { if (!isFloorplanMode) { mapRef?.panTo({ lat: h.lat, lng: h.lng }); mapRef?.setZoom(20); } handleHotspotClick(h); }}
                onMouseEnter={() => setHoveredHotspotId(h.id)}
                onMouseLeave={() => setHoveredHotspotId(null)}
                className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm hover:bg-white/10 transition-colors"
                style={{ color: textColor }}
              >
                <span>{h.room_label}</span>
                <HotspotIconInList type={h.icon_type} color={textColor} iconUrl={h.icon_url} />
              </button>
            ))}
          </div>
        </aside>
        )}

        {/* Room list strip (bottom) */}
        {!isEmbed && showRoomsList && roomsListPosition === 'bottom' && (
        <aside className="w-full shrink-0 border-t px-3 py-2 overflow-x-auto" style={{ backgroundColor: sidebarBg, borderColor: '#3d3d3d' }}>
          <p className="text-xs font-semibold mb-2 uppercase tracking-wider opacity-80 shrink-0" style={{ color: textColor }}>Rooms</p>
          <div className="flex flex-wrap gap-1.5">
            {floorplanHotspots.map((h) => (
              <button
                key={h.id}
                onClick={() => { if (!isFloorplanMode) { mapRef?.panTo({ lat: h.lat, lng: h.lng }); mapRef?.setZoom(20); } handleHotspotClick(h); }}
                onMouseEnter={() => setHoveredHotspotId(h.id)}
                onMouseLeave={() => setHoveredHotspotId(null)}
                className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm hover:bg-white/10 transition-colors shrink-0"
                style={{ color: textColor }}
              >
                <HotspotIconInList type={h.icon_type} color={textColor} iconUrl={h.icon_url} />
                <span>{h.room_label}</span>
              </button>
            ))}
          </div>
        </aside>
        )}
      </div>

      {/* Hover preview near the hovered hotspot (falls back to corner when no anchor exists). */}
      {(() => {
        void is360CacheVer;
        const hoveredHotspot = hoveredHotspotId ? floorplanHotspots.find((h) => h.id === hoveredHotspotId) ?? null : null;
        const showHover = hoverPreviewVisible && hoveredHotspot?.photo_url && !selected;
        if (!showHover) return null;
        const hoverIs360 = hoveredHotspot?.photo_url ? (is360CacheRef.current.get(hoveredHotspot.photo_url) ?? false) : false;
        const previewWidth = 560;
        const imgHeight = 400;
        const previewHeight = imgHeight + 48;
        const margin = 12;
        const gap = 18;
        const viewportW = typeof window !== 'undefined' ? window.innerWidth : 1280;
        const viewportH = typeof window !== 'undefined' ? window.innerHeight : 720;
        const fallbackLeft =
          roomsListPosition === 'right'
            ? margin
            : Math.max(margin, viewportW - previewWidth - margin);
        const fallbackTop = Math.max(margin, viewportH - previewHeight - margin);
        let left = fallbackLeft;
        let top = fallbackTop;
        if (hoverPreviewAnchor) {
          left = hoverPreviewAnchor.x + gap;
          if (left + previewWidth > viewportW - margin) {
            left = hoverPreviewAnchor.x - previewWidth - gap;
          }
          left = Math.min(Math.max(margin, left), Math.max(margin, viewportW - previewWidth - margin));
          top = hoverPreviewAnchor.y - previewHeight / 2;
          top = Math.min(Math.max(margin, top), Math.max(margin, viewportH - previewHeight - margin));
        }
        return (
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="fixed z-40 rounded-xl border overflow-hidden shadow-2xl pointer-events-none"
              style={{
                backgroundColor: sidebarBg,
                borderColor: '#3d3d3d',
                width: previewWidth,
                left,
                top,
              }}
            >
              <div className="w-full bg-black/80 flex items-center justify-center overflow-hidden" style={{ height: imgHeight }}>
                {hoverIs360 ? (
                  <Suspense fallback={<div className="w-full h-full bg-black flex items-center justify-center text-slate-400 text-xs">Loading 360°...</div>}>
                    <LazyMini360Preview src={hoveredHotspot!.photo_url!} width={previewWidth} height={imgHeight} />
                  </Suspense>
                ) : (
                  <img
                    src={hoveredHotspot!.photo_url!}
                    alt={hoveredHotspot!.room_label}
                    className="w-full h-full object-contain"
                  />
                )}
              </div>
              <div className="px-3 py-2.5 border-t" style={{ borderColor: '#3d3d3d' }}>
                <p className="text-sm font-medium truncate" style={{ color: textColor }}>{hoveredHotspot!.room_label}</p>
                <p className="text-xs opacity-70 mt-0.5" style={{ color: textColor }}>{hoverIs360 ? 'Click to open 360° view' : 'Click to open full view'}</p>
              </div>
            </motion.div>
          </AnimatePresence>
        );
      })()}

      {/* Media popup — same card style for image and 360; 360 has embedded viewer + fullscreen option */}
      <AnimatePresence>
        {selected && selected.photo_url && is360Image && show360Fullscreen ? (
          <Viewer360
            imageUrl={selected.photo_url}
            onClose={() => setShow360Fullscreen(false)}
            title={selected.room_label}
            primaryColor={primary}
          />
        ) : null}
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)' }}
            onClick={() => { setSelected(null); setIs360Image(false); setShow360Fullscreen(false); }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              className={`w-full rounded-2xl border overflow-hidden relative shadow-2xl flex flex-col ${is360Image ? 'max-w-[96vw] h-[92vh]' : 'max-w-5xl max-h-[95vh]'}`}
              style={{ backgroundColor: sidebarBg, borderColor: '#3d3d3d' }}
              onClick={(e) => e.stopPropagation()}
            >
              <button onClick={() => { setSelected(null); setIs360Image(false); setShow360Fullscreen(false); }} className="absolute top-3 right-3 z-10 p-2.5 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors shadow-lg" aria-label="Close">
                <X size={20} />
              </button>
              <h2 className={`font-semibold px-6 ${is360Image ? 'text-base pt-3 pb-2' : 'text-lg pt-6 pb-3'}`} style={{ borderBottom: '1px solid #3d3d3d', color: textColor }}>{selected.room_label}</h2>
              <div className={`flex-1 min-h-0 flex flex-col gap-4 ${is360Image ? 'p-2 overflow-hidden' : 'p-6 overflow-y-auto'}`}>
                {selected.photo_url && is360Image ? (
                  <Viewer360
                    imageUrl={selected.photo_url}
                    onClose={() => {}}
                    primaryColor={primary}
                    embedded
                    onFullscreenRequest={() => setShow360Fullscreen(true)}
                    className="rounded-xl overflow-hidden flex-1"
                  />
                ) : selected.photo_url ? (
                  <HotspotImage src={selected.photo_url} alt={selected.room_label} onIs360={setIs360Image} />
                ) : null}
                {selected.video_url && (() => {
                  const embedUrl = getVideoEmbedUrl(selected.video_url);
                  return embedUrl ? (
                    <VideoEmbedWithLoader embedUrl={embedUrl} />
                  ) : null;
                })()}
                {selected.download_url && (
                  <a href={selected.download_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors hover:opacity-90" style={{ backgroundColor: primary, color: 'white' }}>
                    <Download size={16} />
                    Download PDF / brochure
                  </a>
                )}
                {!selected.photo_url && !selected.video_url && !selected.download_url && (
                  <div className="w-full h-48 rounded-xl border border-dashed border-slate-600 flex items-center justify-center text-slate-400 text-sm">No media attached.</div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Powered by planos.studio */}
      {showPoweredBy && (
        <footer className="shrink-0 py-2 px-4 flex items-center justify-center gap-2" style={{ backgroundColor: sidebarBg, borderColor: '#3d3d3d', borderTop: '1px solid #3d3d3d' }}>
          <span className="text-xs opacity-80" style={{ color: textColor }}>Powered by</span>
          <a href="https://planos.rehub.studio" target="_blank" rel="noopener noreferrer" className="text-xs font-medium hover:underline" style={{ color: primary }}>
            planos.studio
          </a>
        </footer>
      )}
    </div>
  );
};

export default PlanosViewer;
